// src/api/services/authService.js
import api from '../interceptors/axiosInterceptor';
import API_CONFIG from '../config';
import router from '@/router';
const AUTH_ENDPOINTS = {
  LOGIN: '/user/login/',
  REGISTER: '/user/register/',
  VERIFY_CODE: '/user/verify/',
  RESEND_CODE: '/user/resend-code/',
  FORGOT_PASSWORD: '/user/forgot-password/',
  RESET_PASSWORD: '/user/reset-password/',
  LOGOUT: '/user/logout/',
  PROFILE: '/user/profile/',
  CHANGE_PASSWORD: '/user/change-password/',
  UPDATE_PROFILE: '/user/profile/update/',
  REFRESH_TOKEN: '/auth/refresh',
  SEND_OTP: '/user/send-otp/',
  VERIFY_OTP: '/user/verify-otp/',
};

const storeSlug = window.__STORE_INFO__?.slug;

class AuthService {

  extractToken(responseData) {
    if (!responseData) return null;
    if (responseData.token) return responseData.token;
    if (responseData.access_token) return responseData.access_token;
    if (responseData.jwt) return responseData.jwt;

    if (responseData.data) {
      if (responseData.data.token) return responseData.data.token;
      if (responseData.data.access_token) return responseData.data.access_token;
      if (responseData.data.jwt) return responseData.data.jwt;

      const innerObj = typeof responseData.data === 'object' ? responseData.data : {};
      if (innerObj.token) return innerObj.token;
    }

    for (const key in responseData) {
      if (key.includes('token') && typeof responseData[key] === 'string' && responseData[key].length > 20) {
        return responseData[key];
      }
    }
    return null;
  }

  async sendOTP(phone) {
    try {
      let normalizedPhone = phone.replace(/\D/g, '');
      if (normalizedPhone.startsWith('9')) normalizedPhone = '0' + normalizedPhone;

      if (!normalizedPhone) throw new Error('شماره موبایل معتبر نیست');

      let baseUrl = API_CONFIG.BASE_URL.replace(/\/$/, '');
      const finalUrl = `${baseUrl}${AUTH_ENDPOINTS.SEND_OTP}${storeSlug}/`;


      const response = await api.options(finalUrl, { phone: normalizedPhone });
      return { success: true, data: response.data, message: 'کد تأیید ارسال شد' };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async verifyOTP(phone, code, purpose = 'login') {
    try {
      let normalizedPhone = phone.replace(/\D/g, '');
      if (normalizedPhone.startsWith('9')) normalizedPhone = '0' + normalizedPhone;

      let baseUrl = API_CONFIG.BASE_URL.replace(/\/$/, '');
      const finalUrl = `${baseUrl}${AUTH_ENDPOINTS.VERIFY_OTP}`;

      const payload = {
        phone: normalizedPhone,
        code: code,
        purpose: purpose,
        store_slug: storeSlug
      };

      const response = await api.post(finalUrl, payload);
      return { success: true, data: response.data, message: 'کد تأیید شد' };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async loginWithVerificationCode(phone, code) {
    try {

      let normalizedPhone = phone.replace(/\D/g, '');
      if (normalizedPhone.startsWith('9')) normalizedPhone = '0' + normalizedPhone;

      let baseUrl = API_CONFIG.BASE_URL.replace(/\/$/, '');
      const finalUrl = `${baseUrl}${AUTH_ENDPOINTS.LOGIN}`;

      const payload = {
        store_slug: storeSlug,
        phone: normalizedPhone,
        code: code,
        login_type: 'code',
        password: code
      };


      const response = await api.post(finalUrl, payload);

      const token = this.extractToken(response.data);
      if (token) {
        this.saveAuthData({ ...response.data, token });
        return { success: true, data: response.data, message: 'ورود موفقیت‌آمیز بود' };
      } else {
        throw new Error('سرور توکن اعتبارسنجی را ارسال نکرد.');
      }
    } catch (error) {
      // نمایش جزئیات خطای 400 برای دیباگ
      if (error.response) {
      }

      if (error.code === 'USER_NOT_FOUND' ||
        error.status === 404 ||
        (error.response?.data?.code === 'USER_NOT_FOUND') ||
        (error.response?.data?.message && error.response.data.message.includes('not found'))) {

        const userNotFoundError = new Error('این شماره موبایل در سیستم یافت نشد. لطفاً ابتدا ثبت‌نام کنید.');
        userNotFoundError.code = 'USER_NOT_FOUND';
        userNotFoundError.isRegistrationRequired = true;
        throw userNotFoundError;
      }

      throw this.handleError(error);
    }
  }

  async loginWithPassword(phone, password) {
    try {
      let baseUrl = API_CONFIG.BASE_URL.replace(/\/$/, '');
      const finalUrl = `${baseUrl}${AUTH_ENDPOINTS.LOGIN}`;

      let normalizedPhone = phone.replace(/\D/g, '');
      if (normalizedPhone.startsWith('9')) normalizedPhone = '0' + normalizedPhone;

      const response = await api.post(finalUrl, {
        phone: normalizedPhone,
        password,
        store_slug: storeSlug
      });

      const token = this.extractToken(response.data);

      if (token) {
        this.saveAuthData({ ...response.data, token });
        return { success: true, data: response.data, message: 'ورود موفقیت‌آمیز بود' };
      } else {
        throw new Error('سرور توکن اعتبارسنجی را ارسال نکرد.');
      }
    } catch (error) {
      if (error.status === 401) {
        throw new Error("نام کاربری یا رمز عبور اشتباه است")
      }
      if (error.status === 404) {
        return new Error('این شماره موبایل در سیستم یافت نشد. لطفاً ابتدا ثبت‌نام کنید.');

      }
      if (error.code === 'USER_NOT_FOUND' ||
        error.status === 404 ||
        (error.response?.data?.code === 'USER_NOT_FOUND')) {

        const userNotFoundError = new Error('این شماره موبایل در سیستم یافت نشد. لطفاً ابتدا ثبت‌نام کنید.');
        userNotFoundError.code = 'USER_NOT_FOUND';
        userNotFoundError.isRegistrationRequired = true;
        throw userNotFoundError;
      }

      if (error.code === 'INVALID_CREDENTIALS') {
        throw error;
      }

      throw this.handleError(error);
    }
  }

  async startRegistration(phone) {
    return await this.sendOTP(phone);
  }

  async verifyRegistrationCode(phone, code) {
    try {
      const response = await this.verifyOTP(phone, code, 'register');
      if (response.data && response.data.temp_token) {
        localStorage.setItem('temp_reg_token', response.data.temp_token);
        localStorage.setItem('register_phone', this.normalizePhoneNumber(phone));
      }
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async completeRegistration(userData) {
    try {
      const tempToken = localStorage.getItem('temp_reg_token');

      if (!userData.phone || !userData.password || !userData.first_name) {
        throw new Error('اطلاعات کاربر ناقص است');
      }

      let normalizedPhone = userData.phone.replace(/\D/g, '');
      if (normalizedPhone.startsWith('9')) normalizedPhone = '0' + normalizedPhone;

      const payload = {
        phone: normalizedPhone,
        first_name: userData.first_name,
        last_name: userData.last_name || '',
        password: userData.password,
        confirm_password: userData.confirm_password,
        role: userData.role || 'customer',
        store_slug: storeSlug
      };

      let baseUrl = API_CONFIG.BASE_URL.replace(/\/$/, '');
      const finalUrl = `${baseUrl}${AUTH_ENDPOINTS.REGISTER}`;

      const headers = {};
      if (tempToken) headers['Authorization'] = `Token ${tempToken}`;


      const response = await api.post(finalUrl, payload, { headers });

      const token = this.extractToken(response.data);
      if (token) {
        this.saveAuthData({ ...response.data, token });
      } else {
        throw new Error('توکن ثبت‌نام یافت نشد.');
      }

      localStorage.removeItem('temp_reg_token');
      localStorage.removeItem('register_phone');

      return { success: true, data: response.data, message: 'ثبت‌نام انجام شد' };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async forgotPassword(phone) {
    try {
      let normalizedPhone = phone.replace(/\D/g, '');
      if (normalizedPhone.startsWith('9')) normalizedPhone = '0' + normalizedPhone;

      localStorage.setItem('reset_password_phone', normalizedPhone);
      let baseUrl = API_CONFIG.BASE_URL.replace(/\/$/, '');
      const finalUrl = `${baseUrl}${AUTH_ENDPOINTS.FORGOT_PASSWORD}`;

      const response = await api.post(finalUrl, {
        phone: normalizedPhone,
        store_slug: storeSlug
      });

      return { success: true, data: response.data, message: 'کد بازیابی ارسال شد', wait_time: response.data?.wait_time || 120 };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async resetPassword(phone, code, newPassword, confirmPassword) {
    try {
      let normalizedPhone = phone.replace(/\D/g, '');
      if (normalizedPhone.startsWith('9')) normalizedPhone = '0' + normalizedPhone;

      const passwordValidation = this.validatePassword(newPassword);
      if (!passwordValidation.isValid) throw new Error(passwordValidation.errors.join(' - '));
      if (newPassword !== confirmPassword) throw new Error('رمز عبور و تکرار آن مطابقت ندارند');

      let baseUrl = API_CONFIG.BASE_URL.replace(/\/$/, '');
      const finalUrl = `${baseUrl}${AUTH_ENDPOINTS.RESET_PASSWORD}`;

      const response = await api.post(finalUrl, {
        phone: normalizedPhone,
        code,
        new_password: newPassword,
        confirm_password: confirmPassword,
        store_slug: storeSlug
      });

      this.clearResetPasswordData();
      const token = this.extractToken(response.data);
      if (token) {
        this.saveAuthData({ ...response.data, token });
      } else {
        throw new Error('توکن بازیابی یافت نشد.');
      }

      return { success: true, data: response.data, message: 'رمز عبور تغییر کرد', auto_login: !!token };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getProfile() {
    const token = this.getToken();
    if (!token) throw new Error('توکن یافت نشد');
    let baseUrl = API_CONFIG.BASE_URL.replace(/\/$/, '');
    const url = `${baseUrl}${AUTH_ENDPOINTS.PROFILE}${storeSlug}/`;

    const response = await api.get(url, { headers: { 'Authorization': `Token ${token}` } });
    return response;
  }

  async updateProfile(data) {
    const token = this.getToken();

    if (!token) {
      throw new Error('توکن اعتبارسنجی یافت نشد. لطفاً مجدداً وارد شوید.');
    }

    let baseUrl = API_CONFIG.BASE_URL.replace(/\/$/, '');
    const url = `${baseUrl}${AUTH_ENDPOINTS.UPDATE_PROFILE}${storeSlug}/`;

    try {
      // *** تغییر مهم: از api.put به api.patch تغییر کرد ***
      const response = await api.patch(url, data, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.user) this.saveUserData(response.data.user);
      return { success: true, data: response.data, message: 'پروفایل بروزرسانی شد' };
    } catch (error) {
      if (error.response?.status === 401) {
        this.clearAuthData();
        router.push('/login');
        throw new Error('توکن منقضی شده است. لطفاً مجدد وارد شوید.');
      }
      return { success: false, message: error.response?.data?.message || 'خطا در بروزرسانی' };
    }
  }


  async changePassword(currentPassword, newPassword, confirmPassword) {
    const token = this.getToken();
    if (!token) throw new Error('توکن یافت نشد');

    let baseUrl = API_CONFIG.BASE_URL.replace(/\/$/, '');
    // *** نکته: طبق لیست شما، این اندپوینت بدون اسلاگ است ***
    const url = `${baseUrl}${AUTH_ENDPOINTS.CHANGE_PASSWORD}`;

    try {
      // ساختار داده برای تغییر رمز عبور
      // گاهی سرور از old_password استفاده می‌کند، گاهی current_password
      // ما هر دو را تست می‌کنیم، اما اولویت با استاندارد رایج است.
      // اگر باز هم خطا داد، ممکن است سرور نیاز به store_slug داشته باشد.

      const payload = {
        old_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
        store_slug: storeSlug
      };


      const response = await api.post(url, payload, {
        headers: { 'Authorization': `Token ${token}` }
      });

      return { success: true, data: response.data, message: 'رمز عبور تغییر کرد' };
    } catch (error) {
      // نمایش جزئیات خطا برای دیباگ
      if (error.response) {
      }
      throw this.handleError(error);
    }
  }

  async logout() {
    const token = this.getToken();
    if (token) {
      let baseUrl = API_CONFIG.BASE_URL.replace(/\/$/, '');
      const url = `${baseUrl}${AUTH_ENDPOINTS.LOGOUT}`;
      try { await api.post(url, {}, { headers: { 'Authorization': `Token ${token}` } }); } catch (e) { }
    }
    this.clearAuthData();
    this.clearResetPasswordData();
    return { success: true, message: 'خروج موفقیت‌آمیز' };
  }

  saveAuthData(authData) {
    const token = this.extractToken(authData);

    if (!token) {
      return;
    }

    this.saveToken(token);
    const refreshToken = authData.refresh_token || authData.data?.refresh_token;
    if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
    if (authData.expires_in) {
      const expiryTime = Date.now() + (authData.expires_in * 1000);
      localStorage.setItem('token_expiry', expiryTime.toString());
    }
    if (authData.user) this.saveUserData(authData.user);
    else if (authData.data?.user) this.saveUserData(authData.data.user);
    api.defaults.headers.common['Authorization'] = `Token ${token}`;
  }


  saveToken(token) {
    if (token) localStorage.setItem('auth_token', token);
  }

  getToken() {
    return localStorage.getItem('auth_token');
  }
  saveUserData(user) {
    const userData = {
      id: user.id,
      phone: user.phone,
      email: user.email || '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      full_name: user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim(),
      avatar: user.avatar || '',
      created_at: user.created_at,
      is_active: user.is_active !== undefined ? user.is_active : true,
      role: user.role || 'customer'
    };
    localStorage.setItem('user', JSON.stringify(userData));
    Object.keys(userData).forEach(key => {
      if (userData[key] !== undefined && userData[key] !== null) {
        localStorage.setItem(`user_${key}`, userData[key].toString());
      }
    });
  }

  clearAuthData() {
    const itemsToRemove = ['auth_token', 'refresh_token', 'token_expiry', 'user', 'remember_me', 'temp_reg_token', 'register_phone', 'verification_phone', 'verification_type'];
    itemsToRemove.forEach(item => localStorage.removeItem(item));
    const userFields = ['id', 'phone', 'email', 'first_name', 'last_name', 'full_name', 'avatar', 'role'];
    userFields.forEach(field => localStorage.removeItem(`user_${field}`));
    delete api.defaults.headers.common['Authorization'];
  }

  isLoggedIn() {
    const token = this.getToken();
    const expiry = localStorage.getItem('token_expiry');
    if (!token) return false;
    if (expiry && Date.now() > parseInt(expiry)) {
      this.clearAuthData();
      return false;
    }
    return true;
  }

  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try { return JSON.parse(userStr); } catch (error) { return null; }
    }
    return null;
  }

  getUserBasicInfo() {
    const user = this.getCurrentUser();
    if (!user) return null;
    return {
      id: user.id,
      name: user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'کاربر',
      phone: user.phone || '',
      email: user.email || '',
      avatar: user.avatar || '',
      initials: this.getUserInitials(user),
      role: user.role || 'customer'
    };
  }

  getUserInitials(user = null) {
    const currentUser = user || this.getCurrentUser();
    if (!currentUser) return 'ک';
    let initials = '';
    if (currentUser.first_name) initials += currentUser.first_name.charAt(0);
    if (currentUser.last_name) initials += currentUser.last_name.charAt(0);
    if (initials === '' && currentUser.full_name) initials = currentUser.full_name.charAt(0);
    if (initials === '' && currentUser.phone) initials = currentUser.phone.slice(-1);
    return initials || 'ک';
  }

  normalizePhoneNumber(phone) {
    if (!phone) return '';
    let normalized = phone.replace(/\D/g, '');
    normalized = normalized.substring(1);
    return normalized;
  }

  formatPhoneForDisplay(phone) {
    if (!phone) return '';
    const normalized = this.normalizePhoneNumber(phone);
    if (normalized.length === 10) return `0${normalized.substring(0, 3)} ${normalized.substring(3, 6)} ${normalized.substring(6)}`;
    return phone;
  }

  validatePassword(password) {
    const errors = [];
    if (!password || password.length < 8) errors.push('حداقل ۸ کاراکتر');
    if (password && !/\d/.test(password)) errors.push('شامل عدد باشد');
    if (password && !/[a-zA-Z]/.test(password)) errors.push('شامل حرف باشد');
    return { isValid: errors.length === 0, errors };
  }

  validatePhoneNumber(phone) {
    const normalized = this.normalizePhoneNumber(phone);
    const iranMobileRegex = /^9[0-9]{9}$/;
    if (!iranMobileRegex.test(normalized)) return { isValid: false, message: 'شماره نامعتبر است' };
    return { isValid: true, normalized: '0' + normalized };
  }

  handleError(error) {
    let errorMessage = 'خطای ناشناخته';
    let errorCode = 'UNKNOWN_ERROR';
    let errorData = null;

    if (error.response) {
      errorData = error.response.data;
      errorMessage = errorData?.message || errorData?.detail || 'خطای سرور';
      errorCode = errorData?.code || `HTTP_${error.response.status}`;

      switch (error.response.status) {
        case 400:
          if (errorData?.code === 'INVALID_PHONE') errorMessage = 'شماره نامعتبر';
          else if (errorData?.code === 'INVALID_OTP') errorMessage = 'کد اشتباه';
          else if (errorData?.code === 'OTP_EXPIRED') errorMessage = 'کد منقضی';
          else if (errorData?.code === 'USER_ALREADY_EXISTS') errorMessage = 'شماره قبلاً ثبت شده';
          else if (errorData?.detail) errorMessage = errorData.detail;
          break;
        case 401:
          if (errorData?.code === 'TOKEN_EXPIRED') errorMessage = 'توکن منقضی';
          else if (errorData?.code === 'INVALID_CREDENTIALS') errorMessage = 'اطلاعات ورود نادرست';
          break;
        case 404:
          if (errorData?.code === 'USER_NOT_FOUND') {
            errorMessage = 'کاربر با این شماره یافت نشد. لطفاً ابتدا ثبت‌نام کنید.';
            errorCode = 'USER_NOT_FOUND';
          } else {
            errorMessage = 'منبع مورد نظر یافت نشد';
            errorCode = 'NOT_FOUND';
          }
          break;
        case 405:
          errorMessage = 'روش درخواست نامعتبر است. لطفاً دوباره تلاش کنید.';
          errorCode = 'METHOD_NOT_ALLOWED';
          break;
        case 429: errorMessage = 'تعداد درخواست زیاد است'; break;
        case 500: errorMessage = 'خطای سرور'; break;
      }
    } else if (error.request) {
      errorMessage = 'اتصال به سرور برقرار نشد';
      errorCode = 'NETWORK_ERROR';
    }

    const customError = new Error(errorMessage);
    customError.code = errorCode;
    customError.data = errorData;
    customError.originalError = error;
    customError.status = error.response?.status;
    throw customError;
  }
}

const authServiceInstance = new AuthService();
export default authServiceInstance;